import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Upload, Loader2, BarChart3, Clock, Users, FileCheck, Target,
  AlertTriangle, CheckCircle, Lightbulb, Activity, Layers, GitCompare,
  ArrowRight, Tag, Plus, Minus, Edit3, ShieldAlert, FileText
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PMIntegrationView = () => {
  const [activeTab, setActiveTab] = useState("import");
  const [loading, setLoading] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [resourceResult, setResourceResult] = useState(null);

  // Baseline compare state
  const [baselineId, setBaselineId] = useState("");
  const [currentId, setCurrentId] = useState("");
  const [compareResult, setCompareResult] = useState(null);
  const [compareLoading, setCompareLoading] = useState(false);
  const [labelEdits, setLabelEdits] = useState({});
  const [savingLabel, setSavingLabel] = useState(null);

  useEffect(() => { fetchSchedules(); }, []);

  const fetchSchedules = async () => {
    try {
      const res = await axios.get(`${API}/pm-integration/schedules`);
      setSchedules(res.data.schedules || []);
    } catch (e) { /* silent */ }
  };

  const handleImport = async (e, source) => {
    const file = e.target.files[0];
    if (!file) return;
    setLoading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      // Universal endpoint handles every supported format (mpp/xer/xml/mpx/pp/...)
      const res = await axios.post(`${API}/pm-integration/import`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const detected = res.data.import_source === "primavera_p6"
        ? "Primavera P6"
        : res.data.import_source === "ms_project"
          ? "MS Project"
          : res.data.import_source === "asta_powerproject"
            ? "Asta PowerProject"
            : (res.data.import_source || "schedule");
      toast.success(`Imported ${res.data.total_activities} activities (${detected})`);
      fetchSchedules();
      setActiveTab("schedules");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally { setLoading(false); }
  };

  const viewSchedule = async (scheduleId) => {
    try {
      const res = await axios.get(`${API}/pm-integration/schedules/${scheduleId}`);
      setSelectedSchedule(res.data);
      setActiveTab("detail");
    } catch (e) { toast.error("Failed to load schedule"); }
  };

  const runAIAnalysis = async (scheduleId) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/pm-integration/schedules/${scheduleId}/ai-analyze`);
      setAnalysisResult(res.data);
      setActiveTab("analysis");
      toast.success("Schedule analysis complete");
    } catch (e) { toast.error(e.response?.data?.detail || "Analysis failed"); }
    finally { setLoading(false); }
  };

  const runResourceLeveling = async (scheduleId) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/pm-integration/schedules/${scheduleId}/resource-leveling`);
      setResourceResult(res.data);
      toast.success("Resource leveling complete");
    } catch (e) { toast.error(e.response?.data?.detail || "Resource leveling failed"); }
    finally { setLoading(false); }
  };

  const saveBaselineLabel = async (scheduleId) => {
    const label = labelEdits[scheduleId];
    if (!label || !label.trim()) return;
    setSavingLabel(scheduleId);
    try {
      await axios.patch(`${API}/pm-integration/schedules/${scheduleId}`, { baseline_label: label.trim(), is_baseline: true });
      toast.success(`Labelled as "${label.trim()}"`);
      fetchSchedules();
      setLabelEdits(prev => { const n = { ...prev }; delete n[scheduleId]; return n; });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save label"); }
    finally { setSavingLabel(null); }
  };

  const runComparison = async () => {
    if (!baselineId || !currentId) { toast.error("Select both programmes"); return; }
    if (baselineId === currentId) { toast.error("Pick two different programmes"); return; }
    setCompareLoading(true);
    setCompareResult(null);
    try {
      const res = await axios.post(`${API}/pm-integration/schedules/compare`, { baseline_id: baselineId, current_id: currentId });
      setCompareResult(res.data);
      toast.success("Comparison ready");
    } catch (e) { toast.error(e.response?.data?.detail || "Compare failed"); }
    finally { setCompareLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid="pm-integration-view">
      <div>
        <h1 className="text-3xl font-black tracking-tight uppercase font-['Chivo']">Programme Integration</h1>
        <p className="text-zinc-500 mt-1">Import P6 / MS Project schedules, label baselines, and compare baseline-vs-baseline to feed delay analysis &amp; claims.</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-zinc-100 border-2 border-zinc-200 flex-wrap h-auto">
          <TabsTrigger value="import" data-testid="tab-import"><Upload size={14} className="mr-1" /> Import</TabsTrigger>
          <TabsTrigger value="schedules" data-testid="tab-schedules"><Layers size={14} className="mr-1" /> Programmes ({schedules.length})</TabsTrigger>
          <TabsTrigger value="compare" data-testid="tab-compare"><GitCompare size={14} className="mr-1" /> Baseline Compare</TabsTrigger>
          {selectedSchedule && <TabsTrigger value="detail" data-testid="tab-detail"><FileCheck size={14} className="mr-1" /> Detail</TabsTrigger>}
          {analysisResult && <TabsTrigger value="analysis" data-testid="tab-analysis"><BarChart3 size={14} className="mr-1" /> Analysis</TabsTrigger>}
        </TabsList>

        <TabsContent value="import">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="border-2 border-black brutal-shadow bg-white p-8 text-center space-y-4">
              <div className="w-16 h-16 bg-gradient-to-br from-rose-600 to-red-700 rounded-xl flex items-center justify-center mx-auto">
                <Activity size={32} className="text-white" />
              </div>
              <h3 className="text-lg font-bold font-['Chivo']">Primavera P6</h3>
              <p className="text-sm text-zinc-500">Import from Oracle Primavera P6 — XER, PMXML, or XML export.</p>
              <label className="block">
                <input type="file" accept=".xer,.pmxml,.xml" onChange={e => handleImport(e, "p6")} className="hidden" data-testid="p6-file-input" />
                <Button asChild disabled={loading} className="bg-[#E11D48] text-white w-full cursor-pointer">
                  <span>{loading ? <><Loader2 size={16} className="mr-2 animate-spin" /> Importing...</> : <><Upload size={16} className="mr-2" /> Upload P6 File</>}</span>
                </Button>
              </label>
              <p className="text-xs text-zinc-400">Supports .xer, .pmxml and .xml — all P6 versions</p>
            </div>

            <div className="border-2 border-black brutal-shadow bg-white p-8 text-center space-y-4">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-yellow-600 rounded-xl flex items-center justify-center mx-auto">
                <Target size={32} className="text-white" />
              </div>
              <h3 className="text-lg font-bold font-['Chivo']">Microsoft Project</h3>
              <p className="text-sm text-zinc-500">Import any MS Project file — native .mpp binary, .mpx, .mpd or MSPDI .xml.</p>
              <label className="block">
                <input type="file" accept=".mpp,.mpx,.mpd,.mdb,.xml" onChange={e => handleImport(e, "msproject")} className="hidden" data-testid="msp-file-input" />
                <Button asChild disabled={loading} className="bg-[#EAB308] text-zinc-900 w-full cursor-pointer">
                  <span>{loading ? <><Loader2 size={16} className="mr-2 animate-spin" /> Importing...</> : <><Upload size={16} className="mr-2" /> Upload MS Project File</>}</span>
                </Button>
              </label>
              <p className="text-xs text-zinc-400">Supports .mpp (98–2021), .mpx, .mpd, .mdb, .xml</p>
            </div>
          </div>

          {/* Universal drop zone for everything else */}
          <div className="mt-6 border-2 border-dashed border-zinc-300 bg-zinc-50 p-6 text-center">
            <p className="text-xs uppercase tracking-wider font-bold text-zinc-500 mb-2">Other Programme Formats</p>
            <p className="text-sm text-zinc-600 mb-3">Asta PowerProject, Phoenix, Synchro, GanttProject, ProjectLibre, FastTrack, SureTrak, P3, SDEF and more — auto-detected on upload.</p>
            <label className="inline-block">
              <input type="file" accept=".pp,.pod,.planner,.gan,.cdpx,.cdpz,.dat,.sdef,.pwa,.prx,.fts,.sch,.sp,.pep,.gnt" onChange={e => handleImport(e, "any")} className="hidden" data-testid="any-file-input" />
              <Button asChild disabled={loading} variant="outline" className="cursor-pointer">
                <span>{loading ? <><Loader2 size={14} className="mr-2 animate-spin" /> Detecting…</> : <><Upload size={14} className="mr-2" /> Upload Any Programme File</>}</span>
              </Button>
            </label>
          </div>
        </TabsContent>

        <TabsContent value="schedules">
          <div className="border-2 border-black brutal-shadow bg-white overflow-hidden">
            {schedules.length === 0 ? (
              <div className="p-12 text-center text-zinc-400">
                <Layers size={48} className="mx-auto mb-4 opacity-30" />
                <p>No programmes imported yet. Import a P6 or MS Project file to get started.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 border-b-2 border-zinc-200">
                    <tr>
                      <th className="text-left p-3 font-bold text-xs uppercase">Project</th>
                      <th className="text-left p-3 font-bold text-xs uppercase">Baseline Label</th>
                      <th className="text-left p-3 font-bold text-xs uppercase">Source</th>
                      <th className="text-left p-3 font-bold text-xs uppercase">Activities</th>
                      <th className="text-left p-3 font-bold text-xs uppercase">Critical</th>
                      <th className="text-left p-3 font-bold text-xs uppercase">Imported</th>
                      <th className="text-left p-3 font-bold text-xs uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {schedules.map((s, i) => (
                      <tr key={s.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                        <td className="p-3 font-medium">{s.project_name}</td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              placeholder={s.baseline_label || "e.g. Baseline 0"}
                              defaultValue={s.baseline_label || ""}
                              onChange={e => setLabelEdits(prev => ({ ...prev, [s.id]: e.target.value }))}
                              className="border border-zinc-300 px-2 py-1 text-xs w-32 focus:border-[#EAB308] outline-none"
                              data-testid={`baseline-label-input-${i}`}
                            />
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-[10px] h-7 px-2"
                              onClick={() => saveBaselineLabel(s.id)}
                              disabled={savingLabel === s.id || !labelEdits[s.id]}
                              data-testid={`save-baseline-label-${i}`}
                            >
                              {savingLabel === s.id ? <Loader2 size={10} className="animate-spin" /> : <Tag size={10} />}
                            </Button>
                          </div>
                        </td>
                        <td className="p-3"><Badge variant="outline">{s.source === "primavera_p6" ? "P6" : "MS Project"}</Badge></td>
                        <td className="p-3">{s.total_activities}</td>
                        <td className="p-3"><span className="text-red-600 font-bold">{s.critical_activities}</span></td>
                        <td className="p-3 text-xs text-zinc-500">{new Date(s.created_at).toLocaleDateString()}</td>
                        <td className="p-3">
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => viewSchedule(s.id)} data-testid={`view-schedule-${i}`}>View</Button>
                            <Button size="sm" className="bg-black text-white" onClick={() => runAIAnalysis(s.id)} disabled={loading} data-testid={`analyze-schedule-${i}`}>
                              {loading ? <Loader2 size={14} className="animate-spin" /> : "AI Analyze"}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>

        {/* ───────────── BASELINE COMPARE ───────────── */}
        <TabsContent value="compare">
          <div className="space-y-6">
            <div className="border-2 border-black brutal-shadow bg-white p-6">
              <div className="flex items-center gap-3 mb-4">
                <GitCompare size={20} className="text-[#E11D48]" />
                <div>
                  <h2 className="text-lg font-bold font-['Chivo']">Baseline vs Baseline Comparison</h2>
                  <p className="text-xs text-zinc-500">Pick two imported programmes. We diff every activity and generate a forensic narrative on the drift.</p>
                </div>
              </div>

              {schedules.length < 2 ? (
                <div className="p-6 border-2 border-dashed border-zinc-200 text-center">
                  <AlertTriangle size={24} className="mx-auto mb-2 text-amber-500" />
                  <p className="text-sm text-zinc-600">You need at least <b>two imported programmes</b> to compare.</p>
                  <Button onClick={() => setActiveTab("import")} className="mt-3 bg-black text-white" data-testid="compare-go-import">
                    <Upload size={14} className="mr-1" /> Import a Programme
                  </Button>
                </div>
              ) : (
                <div className="grid md:grid-cols-[1fr_auto_1fr_auto] gap-4 items-end">
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">Baseline (earlier)</label>
                    <select
                      value={baselineId}
                      onChange={e => setBaselineId(e.target.value)}
                      className="w-full border-2 border-zinc-200 px-3 py-2 text-sm focus:border-[#EAB308] outline-none bg-white"
                      data-testid="select-baseline"
                    >
                      <option value="">— Select baseline —</option>
                      {schedules.map(s => (
                        <option key={s.id} value={s.id}>
                          {(s.baseline_label ? `[${s.baseline_label}] ` : "") + s.project_name + ` (${new Date(s.created_at).toLocaleDateString()})`}
                        </option>
                      ))}
                    </select>
                  </div>
                  <ArrowRight size={18} className="text-zinc-400 mb-3 hidden md:block" />
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">Current (later)</label>
                    <select
                      value={currentId}
                      onChange={e => setCurrentId(e.target.value)}
                      className="w-full border-2 border-zinc-200 px-3 py-2 text-sm focus:border-[#EAB308] outline-none bg-white"
                      data-testid="select-current"
                    >
                      <option value="">— Select current revision —</option>
                      {schedules.map(s => (
                        <option key={s.id} value={s.id}>
                          {(s.baseline_label ? `[${s.baseline_label}] ` : "") + s.project_name + ` (${new Date(s.created_at).toLocaleDateString()})`}
                        </option>
                      ))}
                    </select>
                  </div>
                  <Button
                    onClick={runComparison}
                    disabled={compareLoading || !baselineId || !currentId}
                    className="bg-[#E11D48] text-white h-[42px]"
                    data-testid="btn-run-compare"
                  >
                    {compareLoading ? <Loader2 size={14} className="mr-1 animate-spin" /> : <GitCompare size={14} className="mr-1" />}
                    Compare
                  </Button>
                </div>
              )}
            </div>

            {/* Comparison Results */}
            {compareResult && (
              <div className="space-y-5" data-testid="compare-results">
                {/* Header */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="border-2 border-zinc-200 bg-white p-4">
                    <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Baseline</p>
                    <p className="text-sm font-bold mt-1">{compareResult.baseline.label || compareResult.baseline.project_name}</p>
                    <p className="text-[11px] text-zinc-500">{compareResult.baseline.project_name} • {compareResult.baseline.source}</p>
                  </div>
                  <div className="border-2 border-[#E11D48]/50 bg-[#E11D48]/5 p-4">
                    <p className="text-[10px] uppercase tracking-wider text-[#E11D48] font-bold">Current</p>
                    <p className="text-sm font-bold mt-1">{compareResult.current.label || compareResult.current.project_name}</p>
                    <p className="text-[11px] text-zinc-500">{compareResult.current.project_name} • {compareResult.current.source}</p>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {[
                    { label: "Modified", value: compareResult.stats.modified_count, color: "#F59E0B", icon: Edit3 },
                    { label: "Added", value: compareResult.stats.added_count, color: "#10B981", icon: Plus },
                    { label: "Removed", value: compareResult.stats.removed_count, color: "#DC2626", icon: Minus },
                    { label: "Crit-Path Δ", value: compareResult.stats.critical_changes, color: "#E11D48", icon: ShieldAlert },
                    { label: "Max Slip (d)", value: compareResult.stats.max_finish_slip_days, color: "#EAB308", icon: Clock },
                  ].map((s, i) => {
                    const Icon = s.icon;
                    return (
                      <div key={i} className="border-2 border-zinc-200 bg-white p-4 text-center">
                        <Icon size={14} className="mx-auto mb-1" style={{ color: s.color }} />
                        <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">{s.label}</p>
                        <p className="text-2xl font-black font-['Chivo']" style={{ color: s.color }}>{s.value}</p>
                      </div>
                    );
                  })}
                </div>

                {/* AI Narrative */}
                {compareResult.narrative && (
                  <div className="border-2 border-black bg-zinc-950 text-white p-6 brutal-shadow-lg space-y-4" data-testid="compare-narrative">
                    <div className="flex items-center gap-2">
                      <FileText size={16} className="text-[#E11D48]" />
                      <h3 className="text-sm font-black uppercase tracking-wider font-['Chivo']">AI Forensic Narrative</h3>
                    </div>
                    <p className="text-sm text-zinc-300 leading-relaxed"><b className="text-white">Executive Summary:</b> {compareResult.narrative.executive_summary}</p>
                    <div className="grid md:grid-cols-2 gap-4 text-xs">
                      <div><p className="text-[10px] uppercase tracking-wider text-[#E11D48] font-bold mb-1">Overall Drift</p><p className="text-zinc-400">{compareResult.narrative.overall_drift}</p></div>
                      <div><p className="text-[10px] uppercase tracking-wider text-[#E11D48] font-bold mb-1">Critical Path Changes</p><p className="text-zinc-400">{compareResult.narrative.critical_path_changes}</p></div>
                      <div><p className="text-[10px] uppercase tracking-wider text-[#E11D48] font-bold mb-1">Scope Changes</p><p className="text-zinc-400">{compareResult.narrative.scope_changes}</p></div>
                      <div><p className="text-[10px] uppercase tracking-wider text-[#E11D48] font-bold mb-1">Claims Exposure</p><p className="text-zinc-400">{compareResult.narrative.claims_exposure}</p></div>
                    </div>
                    {compareResult.narrative.likely_causes && compareResult.narrative.likely_causes.length > 0 && (
                      <div>
                        <p className="text-[10px] uppercase tracking-wider text-[#E11D48] font-bold mb-1">Likely Causes</p>
                        <ul className="text-xs text-zinc-400 space-y-1">
                          {compareResult.narrative.likely_causes.map((c, i) => (<li key={i} className="flex gap-2"><span className="text-[#E11D48]">›</span>{c}</li>))}
                        </ul>
                      </div>
                    )}
                    {compareResult.narrative.recommendations && compareResult.narrative.recommendations.length > 0 && (
                      <div>
                        <p className="text-[10px] uppercase tracking-wider text-[#10B981] font-bold mb-1">Recommendations</p>
                        <ul className="text-xs text-zinc-400 space-y-1">
                          {compareResult.narrative.recommendations.map((c, i) => (<li key={i} className="flex gap-2"><span className="text-[#10B981]">›</span>{c}</li>))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {/* Modified Activities */}
                {compareResult.modified.length > 0 && (
                  <div className="border-2 border-zinc-200 bg-white">
                    <div className="px-5 py-3 border-b-2 border-zinc-200 flex items-center justify-between">
                      <h3 className="text-sm font-black uppercase tracking-wide font-['Chivo']">Modified Activities ({compareResult.modified.length})</h3>
                      <Badge className="bg-amber-100 text-amber-700 border-none text-[10px] uppercase">Top 50 shown</Badge>
                    </div>
                    <ScrollArea className="h-[320px]">
                      <table className="w-full text-xs">
                        <thead className="bg-zinc-50 sticky top-0">
                          <tr>
                            <th className="text-left p-2">Activity</th>
                            <th className="text-right p-2">Δ Duration</th>
                            <th className="text-right p-2">Start Shift</th>
                            <th className="text-right p-2">Finish Shift</th>
                            <th className="text-center p-2">Critical</th>
                            <th className="text-right p-2">Δ Progress</th>
                          </tr>
                        </thead>
                        <tbody>
                          {compareResult.modified.slice(0, 50).map((m, i) => (
                            <tr key={i} className="border-b border-zinc-100 hover:bg-zinc-50">
                              <td className="p-2 font-medium">{m.name || m.activity_id}</td>
                              <td className="p-2 text-right font-mono" style={{ color: m.duration_delta_days > 0 ? "#DC2626" : m.duration_delta_days < 0 ? "#10B981" : "#71717A" }}>
                                {m.duration_delta_days > 0 ? "+" : ""}{m.duration_delta_days}d
                              </td>
                              <td className="p-2 text-right font-mono text-zinc-600">{m.start_shift_days ?? "—"}{m.start_shift_days ? "d" : ""}</td>
                              <td className="p-2 text-right font-mono" style={{ color: (m.finish_shift_days || 0) > 0 ? "#DC2626" : (m.finish_shift_days || 0) < 0 ? "#10B981" : "#71717A" }}>
                                {m.finish_shift_days ?? "—"}{m.finish_shift_days ? "d" : ""}
                              </td>
                              <td className="p-2 text-center">
                                {m.criticality_changed ? (
                                  <Badge className={`border-none text-[9px] uppercase ${m.is_critical ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>
                                    {m.was_critical ? "→ off" : "→ on"}
                                  </Badge>
                                ) : m.is_critical ? <span className="text-red-500">●</span> : <span className="text-zinc-300">○</span>}
                              </td>
                              <td className="p-2 text-right font-mono">{m.progress_delta_pct > 0 ? "+" : ""}{m.progress_delta_pct}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </ScrollArea>
                  </div>
                )}

                {/* Added / Removed */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="border-2 border-emerald-200 bg-emerald-50/30 p-4">
                    <h4 className="text-xs font-black uppercase tracking-wide text-emerald-700 mb-2 flex items-center gap-2"><Plus size={14} /> Added Activities ({compareResult.added.length})</h4>
                    {compareResult.added.length === 0 ? <p className="text-xs text-zinc-500">None</p> : (
                      <ul className="text-xs space-y-1 max-h-40 overflow-auto">
                        {compareResult.added.slice(0, 30).map((a, i) => (<li key={i} className="text-zinc-700">• {a.name || a.activity_id} <span className="text-zinc-500">({a.duration_days}d)</span></li>))}
                      </ul>
                    )}
                  </div>
                  <div className="border-2 border-red-200 bg-red-50/30 p-4">
                    <h4 className="text-xs font-black uppercase tracking-wide text-red-700 mb-2 flex items-center gap-2"><Minus size={14} /> Removed Activities ({compareResult.removed.length})</h4>
                    {compareResult.removed.length === 0 ? <p className="text-xs text-zinc-500">None</p> : (
                      <ul className="text-xs space-y-1 max-h-40 overflow-auto">
                        {compareResult.removed.slice(0, 30).map((a, i) => (<li key={i} className="text-zinc-700">• {a.name || a.activity_id} <span className="text-zinc-500">({a.duration_days}d)</span></li>))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        {selectedSchedule && (
          <TabsContent value="detail">
            <div className="border-2 border-black brutal-shadow bg-white p-6 space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold font-['Chivo']">{selectedSchedule.project_name}</h2>
                  <p className="text-sm text-zinc-500">Source: {selectedSchedule.source} | File: {selectedSchedule.filename}</p>
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => runAIAnalysis(selectedSchedule.id)} disabled={loading} className="bg-[#EAB308] text-white" data-testid="detail-ai-analyze">
                    {loading ? <Loader2 size={14} className="mr-1 animate-spin" /> : <BarChart3 size={14} className="mr-1" />} AI Analyze
                  </Button>
                  <Button onClick={() => runResourceLeveling(selectedSchedule.id)} disabled={loading} variant="outline" data-testid="detail-resource-level">
                    <Users size={14} className="mr-1" /> Resource Leveling
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-zinc-50 border text-center">
                  <p className="text-xs font-bold uppercase text-zinc-500">Activities</p>
                  <p className="text-2xl font-black">{selectedSchedule.total_activities}</p>
                </div>
                <div className="p-4 bg-zinc-50 border text-center">
                  <p className="text-xs font-bold uppercase text-zinc-500">Resources</p>
                  <p className="text-2xl font-black text-[#EAB308]">{selectedSchedule.total_resources}</p>
                </div>
                <div className="p-4 bg-zinc-50 border text-center">
                  <p className="text-xs font-bold uppercase text-zinc-500">Critical Path</p>
                  <p className="text-2xl font-black text-[#E11D48]">{selectedSchedule.critical_activities}</p>
                </div>
              </div>

              <div>
                <h3 className="text-xs font-bold uppercase text-zinc-500 mb-3">Activities ({selectedSchedule.activities?.length || 0})</h3>
                <ScrollArea className="h-[400px]">
                  <table className="w-full text-sm">
                    <thead className="bg-zinc-50 sticky top-0">
                      <tr>
                        <th className="text-left p-2 text-xs">ID</th>
                        <th className="text-left p-2 text-xs">Name</th>
                        <th className="text-left p-2 text-xs">Duration</th>
                        <th className="text-left p-2 text-xs">Progress</th>
                        <th className="text-left p-2 text-xs">Critical</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedSchedule.activities || []).map((a, i) => (
                        <tr key={i} className={`border-b border-zinc-100 ${a.is_critical ? 'bg-red-50' : ''}`}>
                          <td className="p-2 text-xs font-mono">{a.activity_id}</td>
                          <td className="p-2 font-medium text-xs">{a.name}</td>
                          <td className="p-2 text-xs">{a.duration_days}d</td>
                          <td className="p-2 text-xs">{a.percent_complete}%</td>
                          <td className="p-2">{a.is_critical && <Badge className="bg-red-100 text-red-700 border-none text-xs">Critical</Badge>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </ScrollArea>
              </div>

              {resourceResult && (
                <div className="mt-6 pt-6 border-t-2 border-zinc-200 space-y-4">
                  <h3 className="text-sm font-bold">Resource Leveling Results</h3>
                  <div className="p-4 bg-blue-50 border border-blue-200 text-center">
                    <p className="text-xs font-bold uppercase text-blue-600">Leveling Score</p>
                    <p className="text-3xl font-black text-[#EAB308]">{resourceResult.leveling_score}/100</p>
                  </div>
                  {resourceResult.conflicts && resourceResult.conflicts.map((c, i) => (
                    <div key={i} className="p-3 border border-amber-200 bg-amber-50">
                      <div className="flex items-center gap-2 mb-1">
                        <AlertTriangle size={14} className="text-amber-600" />
                        <span className="font-medium text-sm">{c.resource}</span>
                      </div>
                      <p className="text-xs text-zinc-600">{c.resolution}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        )}

        {analysisResult && (
          <TabsContent value="analysis">
            <div className="border-2 border-black brutal-shadow bg-white p-6 space-y-6">
              <h2 className="text-lg font-bold font-['Chivo']">AI Schedule Analysis</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-zinc-50 border text-center">
                  <p className="text-xs font-bold uppercase text-zinc-500">Health Score</p>
                  <p className={`text-3xl font-black ${(analysisResult.schedule_health_score || 0) > 70 ? 'text-emerald-600' : 'text-red-600'}`}>{analysisResult.schedule_health_score}</p>
                </div>
                {analysisResult.progress_assessment && (
                  <>
                    <div className="p-4 bg-zinc-50 border text-center">
                      <p className="text-xs font-bold uppercase text-zinc-500">Progress</p>
                      <p className="text-3xl font-black">{((analysisResult.progress_assessment.overall_progress || 0) * 100).toFixed(0)}%</p>
                    </div>
                    <div className="p-4 bg-red-50 border border-red-200 text-center">
                      <p className="text-xs font-bold uppercase text-red-600">Behind Schedule</p>
                      <p className="text-3xl font-black text-red-600">{analysisResult.progress_assessment.behind_schedule_activities}</p>
                    </div>
                    <div className="p-4 bg-emerald-50 border border-emerald-200 text-center">
                      <p className="text-xs font-bold uppercase text-emerald-600">Ahead</p>
                      <p className="text-3xl font-black text-emerald-600">{analysisResult.progress_assessment.ahead_schedule_activities}</p>
                    </div>
                  </>
                )}
              </div>

              {analysisResult.schedule_risks && (
                <div className="space-y-2">
                  <h3 className="text-xs font-bold uppercase text-zinc-500">Schedule Risks</h3>
                  {analysisResult.schedule_risks.map((r, i) => (
                    <div key={i} className="p-3 border border-zinc-200">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium">{r.risk}</span>
                        <Badge className={`border-none text-xs ${r.probability === 'high' ? 'bg-red-100 text-red-700' : r.probability === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>{r.probability}</Badge>
                      </div>
                      <p className="text-xs text-zinc-500">{r.mitigation}</p>
                    </div>
                  ))}
                </div>
              )}

              {analysisResult.optimization_opportunities && (
                <div className="space-y-2">
                  <h3 className="text-xs font-bold uppercase text-zinc-500">Optimization Opportunities</h3>
                  {analysisResult.optimization_opportunities.map((o, i) => (
                    <div key={i} className="p-3 border border-emerald-200 bg-emerald-50 flex items-center gap-3">
                      <Lightbulb size={16} className="text-emerald-600 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm">{o.opportunity}</p>
                        <p className="text-xs text-zinc-500 mt-1">Potential: {o.potential_saving_days}d saved | Effort: {o.effort_required}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {analysisResult.recommendations && (
                <div className="p-4 bg-blue-50 border border-blue-200">
                  <h3 className="text-xs font-bold uppercase text-blue-600 mb-2">Recommendations</h3>
                  <ul className="space-y-1">
                    {analysisResult.recommendations.map((r, i) => (
                      <li key={i} className="text-sm flex items-start gap-2">
                        <CheckCircle size={14} className="text-blue-600 mt-0.5 flex-shrink-0" />
                        <span>{r}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default PMIntegrationView;
